import { z } from 'zod';
import { format, parseISO } from 'date-fns';

/**
 * „Saptamana verde" (T12, p8) -- intervalul in care scoala isi organizeaza activitatile de mediu.
 *
 * Un singur loc pentru campuri si reguli, folosit de DOUA formulare: cererea de inscriere
 * (`schoolRequestSchema`) si detaliile scolii aprobate (`schoolDetailsSchema`, decizie owner
 * 2026-09-15: intervalul se completeaza oricand din profilul scolii). Doua copii ale regulii ar
 * diverge la primul mesaj schimbat.
 *
 * `LocalDate` pe backend -> string ISO `YYYY-MM-DD` pe sarma, exact ce produce `<input type="date">`.
 * Tipul GENERAT spune `Date` (`transformers.gen.ts` nu ruleaza niciodata) -- de-aia schemele care
 * folosesc campurile astea le scot din constrangerea `satisfies`.
 */

/** Sirul gol al unui `<input type="date">` neatins inseamna „lipsa", nu o data invalida. */
const dataOptionala = z.string().optional().transform((val) => (val ? val : undefined));

export const saptamanaVerdeCampuri = {
  saptamanaVerdeStart: dataOptionala,
  saptamanaVerdeEnd: dataOptionala,
  /**
   * UI-only, NU un camp din DTO (owner, 2026-09-16): intervalul a devenit obligatoriu, dar
   * multe scoli inca nu-l stiu la inscriere/actualizare. Bifa asta e singura iesire — nu
   * exista coloana, nu exista reminder programat. Varianta cu coloana dedicata si notificare
   * ulterioara ramane in issue #201, pentru cand ECOTIC decide fluxul; asta e doar o supapa
   * de UI. Se STERGE explicit inainte de trimitere (vezi punctele care construiesc payload-ul
   * de API), altfel ar ajunge ca proprietate necunoscuta in corpul cererii.
   */
  saptamanaVerdeNecunoscuta: z.boolean().optional(),
};

type IntervalValidat = {
  saptamanaVerdeStart?: string;
  saptamanaVerdeEnd?: string;
  saptamanaVerdeNecunoscuta?: boolean;
};

/** Ori amandoua datele, ori niciuna. */
export const ambeleSauNiciuna = (data: IntervalValidat) =>
  (data.saptamanaVerdeStart === undefined) === (data.saptamanaVerdeEnd === undefined);

export const MESAJ_AMBELE_SAU_NICIUNA = {
  message: 'Completează ambele date ale Săptămânii verzi, sau lasă-le pe amândouă goale',
  path: ['saptamanaVerdeEnd'],
};

/**
 * Owner, 2026-09-16: intervalul devine obligatoriu, SAU bifa «Nu cunosc încă intervalul».
 * Impreuna cu `ambeleSauNiciuna` (care ramane activa), rezultatul e: fara bifa, trebuie
 * amandoua datele; cu bifa, un interval PARTIAL (o singura data) tot nu e acceptat -- doar
 * gol sau complet.
 */
export const intervalCompletatSauNecunoscut = (data: IntervalValidat) =>
  data.saptamanaVerdeNecunoscuta === true ||
  (data.saptamanaVerdeStart !== undefined && data.saptamanaVerdeEnd !== undefined);

export const MESAJ_INTERVAL_OBLIGATORIU = {
  message: 'Completează intervalul Săptămânii verzi sau bifează că nu îl cunoști încă',
  path: ['saptamanaVerdeEnd'],
};

/** Puntea intre `<input type="date">`-ul de pe sarma (`YYYY-MM-DD`) si `DatePickerField` (`Date`). */
export function sirLaData(sir?: string): Date | undefined {
  return sir ? parseISO(sir) : undefined;
}

export function dataLaSir(data?: Date): string | undefined {
  return data ? format(data, 'yyyy-MM-dd') : undefined;
}

/** Inceputul nu poate fi dupa sfarsit; o singura zi (start == end) e valida. */
export const startNuDupaSfarsit = (data: IntervalValidat) => {
  if (data.saptamanaVerdeStart === undefined || data.saptamanaVerdeEnd === undefined) return true;
  return data.saptamanaVerdeStart <= data.saptamanaVerdeEnd;
};

export const MESAJ_START_NU_DUPA_SFARSIT = {
  message: 'Data de sfârșit nu poate fi înainte de data de început',
  path: ['saptamanaVerdeEnd'],
};

/**
 * Scoala n-are intervalul desemnat -> se afiseaza avertismentul (decizie owner 2026-09-15: foarte
 * probabil n-o vor avea de la inceput).
 *
 * „Lipsa" = oricare capat lipseste (`undefined`, `null` sau sir gol). Tipurile generate nu spun
 * `| null`, dar backendul intoarce `null` (frontend/CLAUDE.md, pct. 1), iar `unknown` acopera si
 * `Date`-ul mincinos din `types.gen.ts`. Datele se citesc de pe SCOALA INCARCATA, nu din formular:
 * avertismentul dispare dupa o salvare reusita, nu cand utilizatorul tasteaza.
 */
export function saptamanaVerdeLipsa(
  scoala: { saptamanaVerdeStart?: unknown; saptamanaVerdeEnd?: unknown } | null | undefined
): boolean {
  if (!scoala) return false; // inca se incarca sau a picat: nu avertizam despre ce nu stim
  return !scoala.saptamanaVerdeStart || !scoala.saptamanaVerdeEnd;
}
